// ═══════════════════════════════════════════════════════════════════════════
// Acciones de una remisión — un solo juego, tres puertas
//
// Antes, timbrar vivía en Historial, cobrar en Cuentas, y en Clientes no había
// nada. La misma remisión tenía poderes distintos según por dónde llegaras.
//
// Aquí está TODO lo que se le puede hacer a una remisión, una sola vez. Las
// tres pantallas llaman al mismo gancho y muestran la misma barra; lo único
// que cambia entre ellas es cómo encuentras el documento, nunca qué puedes
// hacer con él.
//
// Los diálogos (timbrar, cancelar CFDI, pagos) se montan UNA vez por pantalla,
// no una por remisión: con 43 remisiones en la lista eso eran 43 modales
// dormidos en el árbol.
//
// Qué botones salen y cuáles se bloquean lo decide `dominio/acciones-remision`
// —lógica pura y probada— no este archivo. Aquí solo se dibujan y se ejecutan.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { SAT_MOTIVOS_CANCELACION } from "../fiscal/catalogos-sat.js";
import { cancelarFactura, downloadAcuseCancelacion, downloadFacturapiPdf,
         downloadFacturapiXml, obtenerFacturapi, getFacturapiConfig, timbrarFactura } from "../fiscal/facturapi.js";
import { verConAdjuntos } from "../pdf/guardar.js";
import { fmt } from "../pdf/comunes.js";
import { generateShippingPDF } from "../pdf/envio.js";
import { LOGO_BASE64 } from "../pdf/logo.js";
import { generatePDF } from "../pdf/remision.js";
import { puedeEditarse } from "../dominio/remisiones.js";
import { complementosVivos } from "../dominio/cobranza.js";
import { accionesRemision } from "../dominio/acciones-remision.js";
import { viaDeTimbrado, timbrarEnServidor, cancelarEnServidor, consultarCancelacion,
         descargarDelServidor, obtenerDelServidor, verCfdiConXml,
         estadoLlaveFiscal } from "../fiscal/timbrado-servidor.js";
import { esperaFolio, etiquetaFolio, cancelarRemision, encolarPago,
         cambiarFormaDePago } from "../datos/remisiones-pg.js";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO } from "../fiscal/catalogos-sat.js";
import { ajustarFormaPago, validarPago, puedeCambiarPago, POR_DEFINIR, PPD } from "../dominio/forma-de-pago.js";
import { equipo, asignarAgenteRemision } from "../datos/vendedores.js";
import { comprobarPin, motivoPin } from "../auth/pin.js";
import { supabase } from "../auth/sesion.js";
import { fuenteDeDatos } from "../datos/lectura.js";
import { Label } from "./basicos.jsx";
import { btnPrimary, btnSecondary, selectStyle } from "./estilos.js";
import { PagosPanel } from "./PagosPanel.jsx";
import { programarSincronizacion } from "../datos/sincronizador.js";

// Cada tono dice de qué naturaleza es la acción. El rojo es solo para lo que
// destruye; si todo fuera rojo, el rojo no significaría nada.
const TONOS = {
  neutro:  { borderColor: "#222",   color: "#888" },
  envio:   { borderColor: "#1a3a3a", color: "#70c9c9" },
  exito:   { borderColor: "#1a3a1a", color: "#4ade80" },
  peligro: { borderColor: "#3a1a1a", color: "#f87171" },
  edicion: { borderColor: "#2a2a3a", color: "#a0a0c9" },
};

export function useRemisionAcciones({ clients, products, invoices, setInvoices,
                                      membresia, onAbrirEnEditor, onIrAConfig }) {
  const [timbrarPreview, setTimbrarPreview] = useState(null);
  const [timbrando, setTimbrando] = useState(false);
  const [modoIeps, setModoIeps] = useState("incluido");   // cómo sale el IEPS en el CFDI
  const [cancelandoCfdi, setCancelandoCfdi] = useState(null);
  const [motivoCfdi, setMotivoCfdi] = useState("02");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelPaso, setCancelPaso] = useState("");   // en qué comprobante va
  const [pagosDe, setPagosDe] = useState(null);
  const [reasignando, setReasignando] = useState(null);   // remisión a la que se le cambia el vendedor
  const [cancelandoRem, setCancelandoRem] = useState(null);  // remisión a cancelar, esperando el PIN
  // Qué acción está en curso, por remisión. Bajar un comprobante tarda ~1.4 s
  // —lo genera Facturapi, no nosotros— y sin ninguna señal el usuario cree que
  // el botón no hizo nada y vuelve a picarle.
  const [ocupadas, setOcupadas] = useState({});

  // Estado de la llave EN EL SERVIDOR: si existe y si hay carta responsiva.
  // Nunca su valor. Se consulta una vez; si no hay red se queda en null y se
  // decide con lo local, que es lo que había antes.
  const [llaveServidor, setLlaveServidor] = useState(null);
  useEffect(() => {
    const t = membresia?.tenant_id;
    if (!t || fuenteDeDatos() !== "postgres") { setLlaveServidor(null); return; }
    let vivo = true;
    estadoLlaveFiscal(t).then(e => { if (vivo) setLlaveServidor(e); });
    return () => { vivo = false; };
  }, [membresia?.tenant_id]);

  // Se puede timbrar si hay llave AQUÍ (camino viejo) o ALLÁ (servicio). Antes
  // solo se miraba la local, así que con el timbrado ya en el servidor el
  // botón no aparecía: la app seguía preguntando por algo que ya no usa.
  const llaveEnServidor = !!(llaveServidor?.configurada && llaveServidor?.carta_firmada);
  const puedeFacturar = getFacturapiConfig().enabled || llaveEnServidor;

  // El cliente AL DÍA, no el del snapshot: la CSF pudo subirse después de
  // emitir la remisión, y entonces sí se puede timbrar.
  const clienteDe = (inv) =>
    clients?.find(c => c.id === inv.client?.id) || inv.client;

  const guardarPagos = (actualizada) => {
    const previos = new Set(
      (invoices?.find(i => i.id === actualizada.id)?.payments || []).map(p => p.id));
    setInvoices(prev => prev.map(i => i.id === actualizada.id ? actualizada : i));
    setPagosDe(actualizada);          // el panel sigue abierto y al día

    if (fuenteDeDatos() === "postgres" && membresia?.tenant_id) {
      const nuevos = (actualizada.payments || []).filter(p => !previos.has(p.id));
      for (const pago of nuevos) encolarPago(pago, actualizada, membresia.tenant_id);
      if (nuevos.length) programarSincronizacion();
    }
  };

  // ¿Por dónde va lo fiscal? Por el servidor si se puede — ahí no hace falta
  // proxy de CORS ni llave en el navegador. Si no, por el camino de siempre.
  const via = (inv) => viaDeTimbrado({
    fuente: fuenteDeDatos(), tenantId: membresia?.tenant_id, remision: inv });

  // Las descargas también pasaban por un proxy de terceros. Detrás de un
  // firewall estricto eso simplemente no llega.
  const bajar = async (inv, formato, respaldo) => {
    if (via(inv).via === "servidor")
      return await descargarDelServidor(inv.cfdiId, formato, membresia.tenant_id);
    return await respaldo();
  };

  // El archivo sin hacerle nada, por el camino que toque. Para juntar varios
  // antes de compartirlos.
  const obtener = async (inv, formato) =>
    via(inv).via === "servidor"
      ? await obtenerDelServidor(inv.cfdiId, formato, membresia.tenant_id)
      : await obtenerFacturapi(inv.cfdiId, formato);

  // Los dos documentos de la factura. Antes se traían y se mandaban a la hoja
  // de compartir en el mismo toque, y Safari la niega si el toque ya caducó
  // esperando al servidor. Ahora van al visor del PDF con el XML a bordo, y
  // el botón de ahí comparte o descarga los dos con un toque propio.
  const compartirFactura = async (inv) => {
    const [pdf, xml] = await Promise.all([obtener(inv, "pdf"), obtener(inv, "xml")]);
    await verConAdjuntos(pdf, [xml], `Factura ${etiquetaFolio(inv)}`);
  };

  const clave = (inv, id) => `${inv.id}:${id}`;
  const estaOcupada = (inv, id) => !!ocupadas[clave(inv, id)];

  const ejecutar = async (id, inv) => {
    if (estaOcupada(inv, id)) return;         // doble clic no dispara dos veces
    const cliente = clienteDe(inv);
    const conCliente = { ...inv, client: cliente };
    setOcupadas(o => ({ ...o, [clave(inv, id)]: true }));
    try {
      // Los complementos llevan el id del pago pegado: `pdf_rep:pay_3`. Van
      // siempre por el servidor —son CFDI— y por eso no tienen camino local.
      if (id.startsWith("pdf_rep:") || id.startsWith("xml_rep:")) {
        const pagoId = id.slice(id.indexOf(":") + 1);
        const pago = (inv.payments || []).find(x => x.id === pagoId);
        if (!pago?.rep?.facturapiId) { alert("Ese complemento ya no está disponible."); return; }
        // El PDF del complemento viaja con su XML, igual que en Pagos.
        if (id.startsWith("pdf_"))
          return await verCfdiConXml(pago.rep.facturapiId, membresia.tenant_id,
                                     `Complemento de pago · ${etiquetaFolio(inv)}`);
        return await descargarDelServidor(pago.rep.facturapiId, "xml", membresia.tenant_id);
      }

      switch (id) {
        case "pdf_remision": return generatePDF(conCliente);
        case "pdf_envio":    return generateShippingPDF(conCliente, products);
        case "pdf_cfdi":     return await bajar(inv, "pdf",   () => downloadFacturapiPdf(inv.cfdiId));
        case "xml_cfdi":     return await bajar(inv, "xml",   () => downloadFacturapiXml(inv.cfdiId));
        case "compartir_cfdi": return await compartirFactura(inv);
        case "acuse":        return await bajar(inv, "acuse", () => downloadAcuseCancelacion(inv.cfdiId, "pdf"));
        case "pagos":        return setPagosDe(inv);
        case "reasignar":    return setReasignando(inv);
        case "timbrar":
          // Se propone el modo del cliente; quien timbra lo puede cambiar.
          setModoIeps(cliente?.iepsDisplay === "desglosado" ? "desglosado" : "incluido");
          return setTimbrarPreview(conCliente);
        case "cancelar_cfdi":
          setMotivoCfdi("02");
          return setCancelandoCfdi(inv.id);
        case "consultar_cancelacion": {
          const r = await consultarCancelacion(inv.cfdiId, membresia.tenant_id);
          if (r.estado === "cancelado") {
            setInvoices(prev => prev.map(i => i.id === inv.id ? {
              ...i, cfdiStatus: "canceled", cfdiCancelacionPendiente: false,
              cancelDate: new Date().toISOString(),
            } : i));
            alert("✅ El SAT ya dio el CFDI por cancelado.\n\n" +
                  "La remisión se puede corregir y volver a timbrar.");
          } else if (r.estado === "pendiente") {
            alert("⏳ La cancelación sigue esperando al receptor.\n\n" +
                  "Tiene 72 horas desde la solicitud para aceptarla o rechazarla en el " +
                  "portal del SAT; si no contesta, se da por aceptada. Mientras tanto " +
                  "el CFDI sigue vigente y no se puede volver a timbrar.");
          } else {
            setInvoices(prev => prev.map(i => i.id === inv.id
              ? { ...i, cfdiCancelacionPendiente: false } : i));
            alert(r.estado === "rechazada"
              ? "❌ El receptor RECHAZÓ la cancelación. El CFDI sigue vigente.\n\n" +
                "Puedes volver a solicitarla después de hablar con el cliente."
              : "El SAT no tiene una solicitud de cancelación de este CFDI. Sigue vigente.");
          }
          programarSincronizacion();
          return;
        }
        case "editar": {
          const r = puedeEditarse(inv);
          if (!r.ok) { alert(r.motivo); return; }
          return onAbrirEnEditor?.(inv, "editar");
        }
        case "cancelar_remision": {
          // ── Cancelar EXIGE conexión, y hay que decir por qué ──────────────
          //
          // Cancelar pide PIN, y el PIN lo comprueba el servidor. Un PIN que
          // se pudiera saltar apagando el wifi sería un adorno: bastaría con
          // ponerse en modo avión para destruir documentos.
          //
          // Antes se encolaba y se aplicaba al sincronizar. Eso ya no vale: el
          // vale del PIN dura dos minutos y una cola puede tardar horas, así
          // que la remisión quedaría cancelada aquí y viva en el servidor —
          // dos verdades, que es justo lo que este proyecto evita.
          if (!navigator.onLine) {
            alert("Cancelar necesita conexión.\n\nSe pide tu PIN y eso lo comprueba el " +
                  "servidor. Inténtalo cuando tengas señal.");
            return;
          }
          return setCancelandoRem(inv);
        }
        default: return;
      }
    } catch (e) {
      alert("⚠️ " + (e?.message || e));
    } finally {
      setOcupadas(o => { const n = { ...o }; delete n[clave(inv, id)]; return n; });
    }
  };

  /**
   * La barra de acciones de UNA remisión.
   * @param compacto  en tablas: solo iconos, con el nombre en el tooltip.
   */
  const barraDe = (inv, { compacto = false } = {}) => {
    const acciones = accionesRemision(inv, {
      rol: membresia?.role,
      cliente: clienteDe(inv),
      facturapiActivo: puedeFacturar,
    });
    if (!acciones.length) return null;

    return (
      <div style={{ display: "flex", gap: compacto ? 4 : 8, flexWrap: "wrap",
                    justifyContent: compacto ? "flex-end" : "flex-end",
                    alignItems: "center" }}
           onClick={e => e.stopPropagation()}>
        {acciones.map(a => {
          if (a.esAviso) {
            return (
              <span key={a.id} title={a.etiqueta}
                style={{ fontSize: compacto ? 10 : 11, color: "#facc15",
                         padding: compacto ? "2px 6px" : "8px 0",
                         display: "inline-flex", alignItems: "center", gap: 4 }}>
                {a.icono}{compacto ? "" : ` ${a.etiqueta}`}
              </span>
            );
          }
          // Todo lo que llega aquí se puede ejecutar: el dominio ya no devuelve
          // acciones prohibidas para el rol —no se pintan apagadas, no se
          // pintan— así que no queda estado "bloqueada" que dibujar.
          const tono = TONOS[a.tono] || TONOS.neutro;
          const enCurso = estaOcupada(inv, a.id);
          const base = a.primaria && !compacto
            ? { ...btnPrimary, background: "linear-gradient(135deg, #1a3a1a, #0d2a0d)",
                borderColor: "#2a5a2a", color: "#4ade80" }
            : { ...btnSecondary, ...tono };

          return (
            <button key={a.id}
              title={enCurso ? `${a.etiqueta} — preparando…` : a.etiqueta}
              disabled={enCurso}
              onClick={() => ejecutar(a.id, inv)}
              style={{ ...base,
                padding: compacto ? "2px 6px" : "8px 16px",
                fontSize: compacto ? 10 : 12,
                display: "inline-flex", alignItems: "center", gap: 5,
                cursor: enCurso ? "wait" : "pointer",
                opacity: enCurso ? 0.6 : 1 }}>
              {enCurso ? "⏳" : a.icono}
              {compacto ? "" : ` ${enCurso ? "Preparando…" : a.etiqueta}`}
            </button>
          );
        })}
      </div>
    );
  };

  const invCancelando = cancelandoCfdi
    ? invoices?.find(i => i.id === cancelandoCfdi)
    : null;

  const modales = (
    <>
      {cancelandoRem && (
        <ModalCancelarRemision
          inv={cancelandoRem} tenantId={membresia?.tenant_id}
          onCerrar={() => setCancelandoRem(null)}
          onHecho={(cancelada) => {
            setInvoices(prev => prev.map(i => i.id === cancelada.id ? cancelada : i));
            setCancelandoRem(null);
          }} />
      )}

      {reasignando && (
        <ModalReasignar
          inv={reasignando} tenantId={membresia?.tenant_id}
          onCerrar={() => setReasignando(null)}
          onHecho={() => { setReasignando(null); programarSincronizacion(); }} />
      )}

      {pagosDe && (
        <PagosPanel inv={invoices?.find(i => i.id === pagosDe.id) || pagosDe}
                    onGuardar={guardarPagos}
                    onCerrar={() => setPagosDe(null)}
                    onIrAConfig={onIrAConfig} tenantId={membresia?.tenant_id} />
      )}

      {invCancelando && (
        <ModalCancelarCfdi
          inv={invCancelando} motivo={motivoCfdi} setMotivo={setMotivoCfdi}
          ocupado={cancelBusy} paso={cancelPaso}
          onCerrar={() => !cancelBusy && setCancelandoCfdi(null)}
          onConfirmar={async () => {
            setCancelBusy(true);
            const porServidor = via(invCancelando).via === "servidor";
            const cancelarUno = (id, mot) => porServidor
              ? cancelarEnServidor(id, mot, membresia.tenant_id)
              : cancelarFactura(id, mot);

            // ── Los complementos PRIMERO ──────────────────────────────────
            //
            // Uno por uno y en serie: son solicitudes al SAT y quiero saber
            // exactamente en cuál se quedó si algo truena. Si uno falla se
            // ABANDONA sin tocar la factura — quedarse a medias con la factura
            // cancelada y un REP vivo es justo el estado que hay que evitar.
            //
            // El motivo de un complemento no es el que se eligió para la
            // factura: el 01 exige un CFDI de sustitución, y un REP cancelado
            // porque su factura se va no se sustituye por nada. Va el 02,
            // "comprobante emitido con errores sin relación".
            const reps = complementosVivos(invCancelando);
            try {
              for (const [i, rep] of reps.entries()) {
                setCancelPaso(`Cancelando complemento ${i + 1} de ${reps.length}…`);
                await cancelarUno(rep.facturapiId, "02");
              }
            } catch (e) {
              setCancelPaso("");
              setCancelBusy(false);
              alert(`❌ No se pudo cancelar un complemento de pago: ${e.message}\n\n` +
                    `La FACTURA NO se canceló. Se detuvo aquí a propósito: cancelarla ` +
                    `dejando vivo un complemento deja un comprobante ante el SAT que ` +
                    `ampara el pago de algo que ya no existe.`);
              programarSincronizacion();
              return;
            }

            try {
              if (reps.length) setCancelPaso("Complementos cancelados. Ahora la factura…");
              const r = await cancelarUno(invCancelando.cfdiId, motivoCfdi);
              // Solo SOLICITADA: el receptor tiene que aceptar. El CFDI sigue
              // vigente y la remisión no se puede volver a timbrar todavía.
              if (r.pendiente) {
                setInvoices(prev => prev.map(i => i.id === invCancelando.id
                  ? { ...i, cfdiCancelacionPendiente: true, cancelMotive: motivoCfdi } : i));
                alert(`⏳ Cancelación SOLICITADA, todavía no hecha.\n\n` +
                      `El SAT le pide al receptor que la acepte: tiene 72 horas para ` +
                      `aceptarla o rechazarla en el portal del SAT, y si no contesta se ` +
                      `da por aceptada.\n\nMientras tanto la factura sigue VIGENTE y la ` +
                      `remisión no se puede volver a timbrar. Usa «Consultar cancelación» ` +
                      `en la remisión para ver si ya procedió.`);
                setCancelandoCfdi(null);
                setCancelPaso("");
                setCancelBusy(false);
                programarSincronizacion();
                return;
              }
              setInvoices(prev => prev.map(i => i.id === invCancelando.id ? {
                ...i, cfdiStatus: r.status || "canceled",
                cancelMotive: motivoCfdi, cancelDate: new Date().toISOString(),
                payments: (i.payments || []).map(p => p.rep
                  ? { ...p, rep: { ...p.rep, status: "cancelado" } } : p),
              } : i));
              alert(reps.length
                ? `✅ Cancelados ${reps.length + 1} comprobantes: ${reps.length} ` +
                  `complemento(s) de pago y la factura.\nEstatus: ${r.status || "canceled"}`
                : `✅ Solicitud de cancelación enviada.\nEstatus: ${r.status || "canceled"}`);
              setCancelandoCfdi(null);
            } catch (e) {
              alert(`❌ Error al cancelar la factura: ${e.message}` +
                    (reps.length ? `\n\n⚠️ Los ${reps.length} complemento(s) SÍ se cancelaron. ` +
                                   `La factura sigue vigente y hay que volver a intentarlo.` : ""));
            }
            setCancelPaso("");
            setCancelBusy(false);
            programarSincronizacion();
          }} />
      )}

      {timbrarPreview && (
        <ModalTimbrar
          inv={timbrarPreview} ocupado={timbrando}
          tenantId={membresia?.tenant_id}
          modoIeps={modoIeps} setModoIeps={setModoIeps}
          alCambiarPago={(forma, metodo) => {
            setTimbrarPreview(p => ({ ...p, formaPago: forma, metodoPago: metodo }));
            setInvoices(prev => prev.map(i => i.id === timbrarPreview.id
              ? { ...i, formaPago: forma, metodoPago: metodo } : i));
          }}
          porServidor={via(timbrarPreview).via === "servidor"}
          esLive={via(timbrarPreview).via === "servidor"
            ? !!llaveServidor?.es_live : getFacturapiConfig().isLive}
          onCerrar={() => !timbrando && setTimbrarPreview(null)}
          onConfirmar={async () => {
            const v = via(timbrarPreview);
            const porServidor = v.via === "servidor";
            const isLive = getFacturapiConfig().isLive;
            const ieps = porServidor
              ? `\nIEPS: ${modoIeps === "desglosado" ? "DESGLOSADO" : "incluido en el precio"}` : "";
            const msg = isLive && !porServidor
              ? `⚠️ CONFIRMAR TIMBRADO EN PRODUCCIÓN\n\nCliente: ${timbrarPreview.client.name}\n` +
                `RFC: ${timbrarPreview.client.rfc}\nTotal: ${fmt(timbrarPreview.total)}\n\n¿Estás seguro?`
              : `Confirmar timbrado\n\nCliente: ${timbrarPreview.client.name}\n` +
                `Total: ${fmt(timbrarPreview.total)}${ieps}\n\n¿Continuar?`;
            if (!confirm(msg)) return;
            setTimbrando(true);
            try {
              const r = porServidor
                ? await timbrarEnServidor(timbrarPreview, membresia.tenant_id,
                                          { iepsDisplay: modoIeps })
                : await timbrarFactura(timbrarPreview, products);
              setInvoices(prev => prev.map(i => i.id === timbrarPreview.id ? {
                ...i, client: { ...timbrarPreview.client },
                ...(porServidor ? { iepsDisplay: r._iepsDisplay || modoIeps } : {}),
                cfdiCancelacionPendiente: false,
                cfdiId: r.id, cfdiUuid: r.uuid, cfdiStatus: r.status,
                cfdiVerificationUrl: r.verification_url,
                cfdiSubtotal: r.total !== undefined ? r.subtotal : undefined,
                cfdiDiscount: r.discount, cfdiTotal: r.total,
              } : i));
              setTimbrarPreview(null);
              const dif = r.total !== undefined && Math.abs(r.total - timbrarPreview.total) > 0.5
                ? `\n\n⚠️ Total CFDI (${fmt(r.total)}) difiere por redondeo` : "";
              alert(`✅ CFDI timbrado${porServidor ? " (desde el servidor)" : ""}\n` +
                    `UUID: ${r.uuid || r.id}\n` +
                    `Total: ${r.total !== undefined ? fmt(r.total) : "ver PDF"}${dif}` +
                    (porServidor ? `\nIEPS: ${(r._iepsDisplay || modoIeps) === "desglosado"
                      ? "desglosado" : "incluido en el precio"}` : "") +
                    (r._aviso ? `\n\n⚠️ ${r._aviso}` : ""));
            } catch (e) {
              // Si el CFDI se timbró pero falló el registro, el UUID viene en
              // el error: perderlo sería mucho peor que el fallo mismo.
              alert(`❌ Error: ${e.message}` +
                    (e.uuid ? `\n\n⚠️ EL CFDI SÍ SE TIMBRÓ. Anota el UUID:\n${e.uuid}` : ""));
            }
            setTimbrando(false);
          }} />
      )}
    </>
  );

  /**
   * ¿Esta acción está disponible AHORA para esta remisión?
   *
   * Los atajos (los chips de Cuentas) tienen que preguntar lo mismo que la
   * barra. Si no, reaparece el defecto que este lote vino a quitar: el chip de
   * "por facturar" se ofrecía aunque Facturapi no estuviera configurado, y
   * llevaba a un diálogo que iba a fallar — mientras el botón equivalente,
   * bien informado, no aparecía.
   */
  const accionDisponible = (inv, id) => {
    const a = accionesRemision(inv, {
      rol: membresia?.role,
      cliente: clienteDe(inv),
      facturapiActivo: puedeFacturar,
    }).find(x => x.id === id);
    return !!a && !a.esAviso;
  };

  // `ejecutar` se expone para los atajos: los chips de "por cobrar" y "por
  // facturar" en Cuentas disparan la misma acción que el botón de la barra.
  // Antes, el chip de "por facturar" era un adorno con un tooltip que decía
  // "timbra desde Historial".
  return { barraDe, modales, ejecutar, accionDisponible, abrirPagos: setPagosDe };
}

// ── Diálogos ───────────────────────────────────────────────────────────────

function ModalCancelarCfdi({ inv, motivo, setMotivo, ocupado, paso, onCerrar, onConfirmar }) {
  const elegido = SAT_MOTIVOS_CANCELACION.find(m => m.code === motivo);
  const reps = complementosVivos(inv);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#111", maxWidth: 500, width: "100%", borderRadius: 12,
                 padding: 32, border: "1px solid #3a1a1a" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#f87171", marginBottom: 8 }}>🚫 Cancelar CFDI</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>
          Folio <span style={{ color: "#c9a96e" }}>{esperaFolio(inv) ? "PENDIENTE" : `#${inv.folio}`}</span>
          {" · "}{inv.client?.name}<br />
          UUID: <span style={{ fontFamily: "monospace", fontSize: 11 }}>{inv.cfdiUuid || inv.cfdiId}</span>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Label>Motivo de cancelación (SAT)</Label>
          <select value={motivo} onChange={e => setMotivo(e.target.value)} style={selectStyle}>
            {SAT_MOTIVOS_CANCELACION.map(m => (
              <option key={m.code} value={m.code}>{m.code} – {m.label}</option>
            ))}
          </select>
        </div>

        {elegido?.needsSubstitute && (
          <div style={{ padding: 12, background: "#1f1a0d", borderRadius: 8,
                        border: "1px solid #3a331a", marginBottom: 16, fontSize: 12, color: "#facc15" }}>
            ⚠️ El motivo 01 exige timbrar primero un CFDI de sustitución.
            <br /><span style={{ color: "#888" }}>Todavía no está soportado. Usa motivo 02 o 03.</span>
          </div>
        )}

        {/* ── Lo que se va a cancelar ADEMÁS ──────────────────────────────
            El operador: "para cancelar una factura timbrada que además tiene
            complemento de pago emitido, se cancelan ambos."

            Se enumeran uno por uno en vez de decir "y sus complementos": son
            comprobantes ante el SAT y quien confirma tiene que poder ver
            cuántos y de qué fecha antes de mandarlos todos. */}
        {reps.length > 0 && (
          <div style={{ padding: 12, background: "#1a1408", borderRadius: 8,
                        border: "1px solid #3a331a", marginBottom: 16, fontSize: 12 }}>
            <div style={{ color: "#facc15", marginBottom: 8 }}>
              🧾 Esta factura tiene {reps.length} complemento{reps.length > 1 ? "s" : ""} de
              pago vigente{reps.length > 1 ? "s" : ""}. Se cancela{reps.length > 1 ? "n" : ""} también.
            </div>
            {reps.map(r => (
              <div key={r.pagoId} style={{ color: "#888", paddingLeft: 4 }}>
                · REP del {new Date(r.fecha).toLocaleDateString("es-MX")} por {fmt(r.monto)}
              </div>
            ))}
            <div style={{ color: "#666", marginTop: 8, lineHeight: 1.5 }}>
              Primero los complementos, después la factura: un REP ampara el pago de
              una factura concreta, y si la factura se cancela antes queda un
              comprobante de algo que ya no existe.
            </div>
          </div>
        )}

        <div style={{ padding: 12, background: "#1f0d0d", borderRadius: 8,
                      border: "1px solid #3a1a1a", marginBottom: 20, fontSize: 12, color: "#f87171" }}>
          ⚠️ <strong>Esta acción es irreversible.</strong> La solicitud se envía al SAT.
          {motivo !== "01" && " Si el receptor no necesita aceptar, la cancelación es inmediata."}
          {motivo === "01" && " El receptor tiene 72 horas para aceptar o rechazar."}
        </div>

        {/* Mientras corre, se dice en qué paso va: son varias solicitudes al SAT
            y una puede fallar a media lista. Sin esto, "⏳ Cancelando..." tapa
            cuáles ya se fueron. */}
        {ocupado && paso && (
          <div style={{ padding: 10, background: "#0d1a0d", borderRadius: 8,
                        border: "1px solid #1a3a1a", marginBottom: 16,
                        fontSize: 12, color: "#a8d060" }}>{paso}</div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onCerrar} disabled={ocupado}
            style={{ ...btnSecondary, opacity: ocupado ? 0.4 : 1 }}>Cerrar</button>
          <button disabled={ocupado || elegido?.needsSubstitute} onClick={onConfirmar}
            style={{ ...btnPrimary, background: "linear-gradient(135deg, #3a1a1a, #2a0d0d)",
                     borderColor: "#5a2a2a", color: "#f87171",
                     opacity: (ocupado || elegido?.needsSubstitute) ? 0.4 : 1 }}>
            {ocupado ? "⏳ Cancelando..."
              : reps.length > 0
                ? `🚫 Cancelar ${reps.length + 1} comprobantes`
                : "🚫 Confirmar Cancelación"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalTimbrar({ inv, ocupado, onCerrar, onConfirmar, porServidor, esLive,
                       tenantId, alCambiarPago, modoIeps, setModoIeps }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center",
                  overflow: "auto", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", color: "#111", maxWidth: 700, width: "100%",
                 borderRadius: 8, padding: "32px 40px", marginTop: 20,
                 fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          <img src={LOGO_BASE64} alt="" style={{ width: 48, height: 48, objectFit: "contain" }} />
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, margin: 0,
                         color: "#1a3a1a" }}>Confirmar Timbrado CFDI</h2>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              Folio #{inv.folio} · {new Date(inv.date).toLocaleDateString("es-MX")}
            </div>
          </div>
        </div>

        <div style={{ padding: 16, background: esLive ? "#fef2f2" : "#fefce8", borderRadius: 8,
                      border: esLive ? "2px solid #dc2626" : "2px solid #ca8a04", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: esLive ? "#dc2626" : "#ca8a04",
                        marginBottom: 6 }}>
            {esLive ? "🔴 MODO PRODUCCIÓN — CFDI con validez fiscal" : "🟡 MODO TEST — factura de prueba"}
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            {esLive ? "Se generará un comprobante fiscal digital real ante el SAT."
                    : "Factura de prueba sin validez fiscal."}
          </div>
          {/* De dónde sale la llave. Importa: por el servidor, el comprobante
              se arma desde Postgres y la llave nunca baja a este navegador. */}
          <div style={{ fontSize: 11, color: "#666", marginTop: 8, paddingTop: 8,
                        borderTop: "1px solid #0001" }}>
            {porServidor
              ? "🔒 Se timbra desde el servidor: el comprobante se arma con los datos guardados y la llave no pasa por este dispositivo."
              : "⚠️ Se timbra desde este navegador, con la llave local y a través de un proxy."}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20,
                      padding: 16, background: "#f8f8f8", borderRadius: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: "#888", letterSpacing: 1, marginBottom: 4 }}>CLIENTE</div>
            <div style={{ fontWeight: 600 }}>{inv.client.name}</div>
            <div style={{ fontSize: 12, color: "#666" }}>RFC: {inv.client.rfc}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#888", letterSpacing: 1, marginBottom: 4 }}>DATOS FISCALES</div>
            <div style={{ fontSize: 12, color: "#666" }}>Régimen: {inv.client.taxSystem}</div>
            <div style={{ fontSize: 12, color: "#666" }}>CP: {inv.client.domicilioFiscalZip}</div>
            <PagoEditable inv={inv} tenantId={tenantId} alCambiar={alCambiarPago} />
          </div>
        </div>

        <ElegirIeps inv={inv} porServidor={porServidor} modo={modoIeps}
                    setModo={setModoIeps} ocupado={ocupado} />

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 12 }}>
          <thead><tr style={{ borderBottom: "2px solid #ddd" }}>
            <th style={{ textAlign: "left", padding: "6px 4px", color: "#888", fontSize: 10 }}>PRODUCTO</th>
            <th style={{ textAlign: "center", padding: "6px 4px", color: "#888", fontSize: 10 }}>CANT.</th>
            <th style={{ textAlign: "right", padding: "6px 4px", color: "#888", fontSize: 10 }}>IMPORTE</th>
          </tr></thead>
          <tbody>
            {inv.lines.map((li, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "6px 4px" }}>{li.productName}</td>
                <td style={{ padding: "6px 4px", textAlign: "center" }}>{li.qty}</td>
                <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmt(li.amount || li.unitPrice * li.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", fontSize: 18, fontWeight: 700,
                      marginBottom: 20 }}>Total: {fmt(inv.total)}</div>

        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 12, paddingTop: 16,
                      borderTop: "2px solid #eee" }}>
          <button onClick={onCerrar} disabled={ocupado}
            style={{ padding: "12px 28px", border: "1px solid #ddd", borderRadius: 6,
                     background: "#fff", cursor: "pointer", fontSize: 13, color: "#666" }}>
            Cancelar
          </button>
          <button disabled={ocupado} onClick={onConfirmar}
            style={{ padding: "12px 28px", border: "none", borderRadius: 6,
                     background: ocupado ? "#666" : "linear-gradient(135deg, #1a5a1a, #0d3a0d)",
                     color: "#fff", cursor: ocupado ? "wait" : "pointer", fontSize: 14, fontWeight: 700 }}>
            {ocupado ? "⏳ Timbrando..." : "🏛 Confirmar Timbrado"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cómo sale el IEPS en el comprobante ────────────────────────────────────
//
// El operador: "las facturas para distribuidor deben poder emitirse con IEPS
// desglosado, porque lo pueden deducir. Debe ser una opción al emitir."
//
// Es del COMPROBANTE, no de la remisión: el importe es idéntico y solo cambia
// la presentación. Art. 19 fr. II de la LIEPS: se traslada en forma expresa y
// por separado cuando el adquirente también es contribuyente del IEPS —quien
// revende—; a un consumidor final o un restaurante va incluido en el precio.
// Se propone el modo de la ficha del cliente.
function ElegirIeps({ inv, porServidor, modo, setModo, ocupado }) {
  if (!porServidor) {
    return (
      <div style={{ fontSize: 11, color: "#888", marginBottom: 16 }}>
        Desde este navegador el IEPS sale siempre desglosado.
      </div>
    );
  }
  const opciones = [
    { v: "incluido", titulo: "IEPS incluido en el precio",
      nota: "Consumidor final o restaurante. Solo se traslada IVA." },
    { v: "desglosado", titulo: "IEPS desglosado",
      nota: "Distribuidor que revende: puede acreditar el IEPS. Se trasladan IEPS e IVA por separado." },
  ];
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, color: "#888", letterSpacing: 1, marginBottom: 6 }}>
        IEPS EN EL COMPROBANTE
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
        {opciones.map(o => {
          const activo = modo === o.v;
          return (
            <button key={o.v} type="button" disabled={ocupado} onClick={() => setModo(o.v)}
              aria-pressed={activo}
              style={{ textAlign: "left", padding: "10px 12px", borderRadius: 6, cursor: "pointer",
                       background: activo ? "#eef6ee" : "#fff",
                       border: activo ? "2px solid #1a5a1a" : "1px solid #ddd",
                       color: "#111", fontFamily: "inherit" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{activo ? "● " : "○ "}{o.titulo}</div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 3, lineHeight: 1.4 }}>{o.nota}</div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
        El total no cambia{inv.iepsAmount ? <> — el IEPS de esta remisión es {fmt(inv.iepsAmount)}</> : null}.
      </div>
    </div>
  );
}

// ── De quién es esta venta ─────────────────────────────────────────────────
//
// Existe porque durante meses NADIE lo era: la app nunca mandaba el agente al
// crear, así que las 46 remisiones de producción nacieron huérfanas. Ya nacen
// con responsable (migración 0029); esto es para corregir las que quedaron mal
// y las que se capturaron a nombre de quien no vendió.
//
// Dice de frente lo que cambia y lo que NO: mover al responsable mueve la
// comisión futura, y no toca la tasa. Las dos cosas sorprenden si no se dicen.
function ModalReasignar({ inv, tenantId, onCerrar, onHecho }) {
  const [gente, setGente] = useState(null);
  const [elegido, setElegido] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    let vivo = true;
    equipo(tenantId)
      .then(g => { if (vivo) setGente((g || []).filter(x => x.is_active)); })
      .catch(e => vivo && setError(e.message));
    return () => { vivo = false; };
  }, [tenantId]);

  const guardar = async () => {
    setOcupado(true); setError("");
    try {
      await asignarAgenteRemision(inv._pgId || inv.id, elegido || null, motivo);
      onHecho?.();
    } catch (e) { setError(e.message); setOcupado(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#111", maxWidth: 460, width: "100%", borderRadius: 12,
                 padding: 28, border: "1px solid #2a2a3a" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#a0a0c9", marginBottom: 6 }}>🤝 ¿De quién es esta venta?</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 18 }}>
          Remisión <span style={{ color: "#c9a96e" }}>#{etiquetaFolio(inv)}</span>
          {" · "}{inv.client?.name} · {fmt(inv.total)}
        </div>

        <Label>Vendedor</Label>
        <select value={elegido} onChange={e => setElegido(e.target.value)} style={selectStyle}
          disabled={!gente}>
          <option value="">{gente ? "Sin vendedor" : "Cargando el equipo…"}</option>
          {(gente || []).map(g => (
            <option key={g.membership_id || g.id} value={g.membership_id || g.id}>
              {g.full_name || g.email} · {g.role}
            </option>
          ))}
        </select>

        <div style={{ marginTop: 14 }}>
          <Label>Por qué (queda en la bitácora)</Label>
          <input value={motivo} onChange={e => setMotivo(e.target.value)}
            placeholder="Ej. la levantó Ana, se capturó a mi nombre"
            style={{ ...selectStyle, fontFamily: "inherit" }} />
        </div>

        <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginTop: 14,
                      padding: "10px 12px", background: "#0d0d0d", borderRadius: 6,
                      border: "1px solid #1e1e1e" }}>
          La comisión de esta remisión pasa a quien la reciba. <b style={{ color: "#888" }}>No
          se cambia la tasa</b>: si está en blanco, sigue sin generar comisión.
          <br />
          Si esta comisión ya se pagó, la base no deja moverla — eso se corrige
          revirtiendo el pago.
        </div>

        {error && (
          <div style={{ background: "#1f0d0d", border: "1px solid #3a1a1a", borderRadius: 6,
                        padding: "10px 12px", marginTop: 14, fontSize: 12, color: "#e8b0b0" }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onCerrar} disabled={ocupado} style={btnSecondary}>Cerrar</button>
          <button onClick={guardar} disabled={ocupado || !gente}
            style={{ ...btnPrimary, opacity: (ocupado || !gente) ? 0.5 : 1 }}>
            {ocupado ? "Guardando…" : "Asignar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cancelar una remisión: motivo + PIN ────────────────────────────────────
//
// Antes era un `prompt()` del navegador y se encolaba para subir después. Dos
// cambios, los dos por el PIN:
//
//   · el motivo y el PIN se piden en la misma pantalla, porque son un solo
//     acto: nadie teclea un PIN sin saber qué está confirmando
//   · se aplica CONTRA EL SERVIDOR en el momento, no se encola. El vale del
//     PIN dura dos minutos; una cola puede tardar horas, y la remisión
//     quedaría cancelada aquí y viva allá
//
// Lo que se destruye no se destruye: queda con motivo, quién y cuándo, y el
// folio se consume. Eso es de siempre y aquí se dice, porque quien va a
// cancelar suele temer que se pierda algo.
function ModalCancelarRemision({ inv, tenantId, onCerrar, onHecho }) {
  const [motivo, setMotivo] = useState("");
  const [pin, setPin] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");

  const puede = motivo.trim().length >= 3 && /^[0-9]{4,8}$/.test(pin);

  const cancelar = async () => {
    setOcupado(true); setError("");
    try {
      // 1. El PIN se cambia por un vale. Devuelve el fallo en vez de lanzarlo
      //    —así el conteo de intentos sobrevive— así que hay que mirarlo.
      const r = await comprobarPin(pin);
      if (!r?.ok) { setError(motivoPin(r)); setPin(""); setOcupado(false); return; }

      // 2. Y el vale viaja con la cancelación, en el mismo viaje.
      const { error: err } = await supabase.rpc("cancelar_remision", {
        p_tenant: tenantId, p_legacy: inv.id, p_motivo: motivo.trim(),
        p_pin: r.vale,
      });
      if (err) throw new Error(err.message);

      onHecho?.({ ...inv, canceledAt: new Date().toISOString(),
                  cancelReason: motivo.trim() });
    } catch (e) { setError(e.message); setOcupado(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onCerrar}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#111", maxWidth: 460, width: "100%", borderRadius: 12,
                 padding: 28, border: "1px solid #3a1a1a" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22,
                      color: "#f87171", marginBottom: 6 }}>Cancelar la remisión</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
          <span style={{ color: "#c9a96e" }}>#{etiquetaFolio(inv)}</span>
          {" · "}{inv.client?.name} · {fmt(inv.total)}
        </div>

        <div style={{ fontSize: 11, color: "#888", lineHeight: 1.7, marginBottom: 16,
                      padding: "10px 12px", background: "#0d0d0d", borderRadius: 6,
                      border: "1px solid #1e1e1e" }}>
          La remisión <b style={{ color: "#aaa" }}>no se borra</b>: queda registrada como
          cancelada, con el motivo, quién y cuándo. El folio no se reutiliza.
        </div>

        <Label>Motivo</Label>
        <input value={motivo} onChange={e => { setMotivo(e.target.value); setError(""); }}
          autoFocus placeholder="Ej. se capturó el cliente equivocado"
          style={{ ...selectStyle, fontFamily: "inherit", marginBottom: 14 }} />

        <Label>Tu PIN</Label>
        <input type="password" inputMode="numeric" value={pin} autoComplete="off"
          onChange={e => { setPin(e.target.value.replace(/\D/g, "")); setError(""); }}
          style={{ ...selectStyle, letterSpacing: 6, textAlign: "center",
                   fontFamily: "monospace" }} />
        <div style={{ fontSize: 10, color: "#556", marginTop: 6 }}>
          Se pide porque esto destruye un documento. Si no tienes PIN, se pone en Mi cuenta.
        </div>

        {error && (
          <div style={{ background: "#1f0d0d", border: "1px solid #3a1a1a", borderRadius: 6,
                        padding: "10px 12px", marginTop: 14, fontSize: 12, color: "#e8b0b0" }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onCerrar} disabled={ocupado} style={btnSecondary}>Mejor no</button>
          <button onClick={cancelar} disabled={!puede || ocupado}
            style={{ ...btnPrimary, background: "linear-gradient(135deg, #3a1a1a, #2a0d0d)",
                     borderColor: "#5a2a2a", color: "#f87171",
                     opacity: (!puede || ocupado) ? 0.4 : 1 }}>
            {ocupado ? "Cancelando…" : "Cancelar la remisión"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cómo se paga, cambiable donde te das cuenta ────────────────────────────
//
// El operador describió el rodeo completo: "tengo que salir de la pestaña, ir
// a la de clientes y regresar. Luego volver a localizar la remisión... pero aún
// cuando haya cambiado la forma de pago y el método de pago la remisión
// continúa igual."
//
// Las dos quejas eran ciertas y la segunda era un defecto: la remisión CONGELA
// cómo se paga al crearse, así que tocarlo en el cliente cambia el valor por
// omisión de las SIGUIENTES y no esta. El usuario daba la vuelta entera para
// que no pasara nada.
//
// Congelarlo está bien —el documento dice lo que decía— y lo que faltaba era
// poder corregirlo AQUÍ, en el momento de timbrar, que es cuando se descubre.
//
// Y la regla del SAT se aplica sola: elegir PPD pone la forma en 99 y la
// bloquea. La combinación imposible no se puede ni construir.
function PagoEditable({ inv, tenantId, alCambiar }) {
  const [abierto, setAbierto] = useState(false);
  const [forma, setForma] = useState(inv.formaPago || "03");
  const [metodo, setMetodo] = useState(inv.metodoPago || "PUE");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");

  const sePuede = puedeCambiarPago(inv);
  const nombre = (lista, code) =>
    lista.find(x => x.code === code)?.label || code || "—";

  const elegirMetodo = (m) => { setMetodo(m); setForma(f => ajustarFormaPago(m, f)); setError(""); };

  const guardar = async () => {
    const v = validarPago({ metodo, forma });
    if (!v.ok) { setError(v.motivo); return; }
    setOcupado(true); setError("");
    try {
      await cambiarFormaDePago(tenantId, inv.id, forma, metodo);
      alCambiar?.(forma, metodo);
      setAbierto(false);
    } catch (e) { setError(e.message); }
    setOcupado(false);
  };

  if (!abierto) {
    return (
      <div style={{ fontSize: 12, color: "#666", display: "flex", gap: 8,
                    alignItems: "baseline", flexWrap: "wrap" }}>
        <span>
          Pago: {nombre(SAT_FORMA_PAGO, inv.formaPago)} · {nombre(SAT_METODO_PAGO, inv.metodoPago)}
        </span>
        {sePuede.ok ? (
          <button type="button" onClick={() => setAbierto(true)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                     color: "#1a5a1a", fontSize: 11, textDecoration: "underline",
                     fontFamily: "inherit" }}>
            cambiar
          </button>
        ) : (
          <span title={sePuede.motivo} style={{ fontSize: 10, color: "#999" }}>
            (ya no se puede cambiar)
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6, padding: 12, background: "#f8f8f8", borderRadius: 6,
                  border: "1px solid #ddd" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>MÉTODO</div>
          <select value={metodo} onChange={e => elegirMetodo(e.target.value)}
            style={{ width: "100%", padding: "7px 8px", fontSize: 12, borderRadius: 4,
                     border: "1px solid #ccc" }}>
            {SAT_METODO_PAGO.map(m => (
              <option key={m.code} value={m.code}>{m.code} – {m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#888", marginBottom: 3 }}>FORMA</div>
          <select value={forma} onChange={e => { setForma(e.target.value); setError(""); }}
            disabled={metodo === PPD}
            style={{ width: "100%", padding: "7px 8px", fontSize: 12, borderRadius: 4,
                     border: "1px solid #ccc", background: metodo === PPD ? "#eee" : "#fff" }}>
            {SAT_FORMA_PAGO.map(f => (
              <option key={f.code} value={f.code}>{f.code} – {f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {metodo === PPD && (
        <div style={{ fontSize: 11, color: "#8a6d1a", marginTop: 8, lineHeight: 1.6 }}>
          En parcialidades la forma es forzosamente <b>{POR_DEFINIR} — por definir</b>:
          todavía no se sabe cómo van a pagar. Cada pago que registres después llevará su
          complemento, y ahí se dice cómo entró.
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#b00", marginTop: 8 }}>⚠️ {error}</div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => { setAbierto(false); setError("");
                                               setForma(inv.formaPago || "03");
                                               setMetodo(inv.metodoPago || "PUE"); }}
          style={{ padding: "6px 14px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                   border: "1px solid #ddd", background: "#fff", color: "#666" }}>
          Cancelar
        </button>
        <button type="button" onClick={guardar} disabled={ocupado}
          style={{ padding: "6px 14px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                   border: "none", background: "#1a5a1a", color: "#fff",
                   opacity: ocupado ? 0.5 : 1 }}>
          {ocupado ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}
